import { cx } from '@flexbyt/widget-preview';
import { useState } from 'react';

/**
 * The rating control — the one field this page exists to collect.
 *
 * ---------------------------------------------------------------------------
 * Radio buttons, not five click handlers
 * ---------------------------------------------------------------------------
 * The legacy form drew five `<span>`s with `onClick`, which meant the single
 * most important input on the page could not be reached by keyboard and was
 * announced as nothing at all. This is a real radio group: arrow keys move
 * between the stars, the whole group takes one tab stop, and each option has a
 * name a screen reader can say.
 *
 * The visible stars are drawn from the inputs' own state rather than from a
 * `selected` prop, so there is no second source of truth to fall out of step —
 * `peer-checked` and `peer-focus-visible` do the work in CSS.
 */
/** How the form asks for its 1–5 rating — see the form's Appearance settings. */
export type RatingStyle = 'stars' | 'faces' | 'hearts';

const FACES = ['😞', '😕', '😐', '🙂', '😍'];
const STAR_PATH =
  'M10 1.4l2.5 5.07 5.6.82-4.05 3.95.96 5.57L10 14.19l-5.01 2.62.96-5.57L1.9 7.29l5.6-.82z';
const HEART_PATH =
  'M10 17.3 8.8 16.2C4.4 12.3 1.6 9.8 1.6 6.6 1.6 4.1 3.6 2.1 6.1 2.1c1.4 0 2.8.7 3.9 1.8 1.1-1.1 2.5-1.8 3.9-1.8 2.5 0 4.5 2 4.5 4.5 0 3.2-2.8 5.7-7.2 9.6L10 17.3z';

/** Faces are one choice each; stars and hearts fill up to the value. */
export function isLit(style: RatingStyle, mark: number, value: number) {
  return style === 'faces' ? mark === value : mark <= value;
}

/** One mark of the scale: a star, a heart, or the face for that value. */
export function RatingMark({
  style,
  mark,
  lit,
  color,
  className,
}: {
  style: RatingStyle;
  mark: number;
  lit: boolean;
  color: string;
  className?: string;
}) {
  if (style === 'faces') {
    return (
      <span
        aria-hidden
        className={cx('inline-grid place-items-center leading-none transition-[transform,opacity,filter] duration-200', className)}
        // Sized by the caller's text class (`text-[3rem]` on the first
        // screen), so a face is as big as the star it stands in for.
        style={{
          opacity: lit ? 1 : 0.5,
          filter: lit ? undefined : 'grayscale(1)',
          transform: lit ? 'scale(1.15)' : undefined,
        }}
      >
        {FACES[mark - 1]}
      </span>
    );
  }
  return (
    <svg viewBox="0 0 20 20" aria-hidden className={className}>
      <path d={style === 'hearts' ? HEART_PATH : STAR_PATH} fill={color} opacity={lit ? 1 : 0.3} />
    </svg>
  );
}

export function Stars({
  value,
  onChange,
  onPick,
  color,
  label,
  size = 'md',
  ratingStyle = 'stars',
}: {
  value: number;
  onChange: (rating: number) => void;
  /**
   * A star chosen with a tap or a click — the gesture that means "this one".
   * Not fired by the arrow keys, which move through the stars one at a time
   * and would otherwise jump the reviewer to the next screen on the first
   * press.
   */
  onPick?: (rating: number) => void;
  color: string;
  label: string;
  /** `lg` for the first screen, where choosing a rating is the whole question. */
  size?: 'md' | 'lg';
  ratingStyle?: RatingStyle;
}) {
  /*
    Hover previews the rating the way the legacy control did, but only for a
    pointer. It is deliberately not wired to focus: arrowing through a radio
    group already *changes* the value, so a focus-driven preview would just be
    the value again, drawn twice.
  */
  const [hovered, setHovered] = useState(0);
  const shown = hovered || value;

  return (
    <fieldset className={size === 'lg' ? 'mt-7' : 'mt-4'}>
      <legend className="sr-only">{label}</legend>
      <div
        className={cx('flex', size === 'lg' ? 'justify-center gap-2 sm:gap-3' : 'gap-1.5')}
        onPointerLeave={() => setHovered(0)}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <label
            key={star}
            className="cursor-pointer"
            onPointerEnter={() => setHovered(star)}
            onPointerUp={() => onPick?.(star)}
          >
            <input
              type="radio"
              name="rating"
              value={star}
              checked={value === star}
              onChange={() => onChange(star)}
              onKeyDown={(event) => {
                // Enter or Space is the keyboard's "this one"; the arrows only
                // move. Enter would otherwise submit the form from a radio.
                if (onPick && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onChange(star);
                  onPick(star);
                }
              }}
              className="peer sr-only"
            />
            <span className="sr-only">
              {star} {star === 1 ? 'star' : 'stars'}
            </span>
            <RatingMark
              style={ratingStyle}
              mark={star}
              lit={isLit(ratingStyle, star, shown)}
              color={color}
              className={cx(
                size === 'lg'
                  ? 'size-12 text-[2.5rem] sm:size-14 sm:text-[2.9rem] transition-transform duration-[var(--duration-fast)] hover:scale-110'
                  : 'size-9 text-[1.9rem] transition-transform duration-[var(--duration-fast)]',
                'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
                'peer-focus-visible:outline-accent-600 peer-focus-visible:rounded-sm'
              )}
            />
          </label>
        ))}
      </div>
    </fieldset>
  );
}
